/**
 * Date serial conversions and parsing for formulas and grid formatting.
 *
 * Excel / Sheets stores dates as serial numbers (floating point days since 1899-12-30).
 * 1 day = 1.0.
 */

const SHEETS_EPOCH_UTC = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 86_400_000;

/** Convert a Date object to Excel day serial number. */
export function dateToSerial(d: Date): number {
  const utcDate = Date.UTC(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds(),
  );
  const serial = (utcDate - SHEETS_EPOCH_UTC) / MS_PER_DAY;
  return Math.abs(serial - Math.round(serial)) < 1e-9 ? Math.round(serial) : serial;
}

/** Convert an Excel day serial number back to a Date object. */
export function serialToDate(serial: number): Date {
  const date = new Date(SHEETS_EPOCH_UTC + Math.round(serial * MS_PER_DAY));
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );
}

/**
 * Parse `d/m`, `d/m/yy`, `dd/mm/yyyy`, ISO `yyyy-mm-dd`, or dates with weekday prefix.
 * If year is omitted (e.g. `13/3` or `16/3`), uses current year.
 */
export function parseGridDate(text: string): Date | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  // 1. ISO date: YYYY-MM-DD
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(trimmed);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // 2. Full date with year: d/m/yyyy or d-m-yyyy (optionally with weekday prefix like 'jue 18/03/2027')
  const matchWithYear = /(?:^|\D)(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:\D|$)/.exec(trimmed);
  if (matchWithYear) {
    const ds = matchWithYear[1];
    const ms = matchWithYear[2];
    const ys = matchWithYear[3];
    if (ds && ms && ys) {
      const dia = Number(ds);
      const mes = Number(ms);
      const anio = ys.length === 2 ? 2000 + Number(ys) : Number(ys);
      if (mes >= 1 && mes <= 12 && dia >= 1) {
        const date = new Date(anio, mes - 1, dia);
        if (date.getFullYear() === anio && date.getMonth() === mes - 1 && date.getDate() === dia) {
          return date;
        }
      }
    }
  }

  // 3. Short date without year: d/m or dd/mm (e.g. '13/3', '16/3')
  const matchShort = /(?:^|\D)(\d{1,2})[/-](\d{1,2})(?:\D|$)/.exec(trimmed);
  if (matchShort) {
    const ds = matchShort[1];
    const ms = matchShort[2];
    if (ds && ms) {
      const dia = Number(ds);
      const mes = Number(ms);
      const anio = new Date().getFullYear();
      if (mes >= 1 && mes <= 12 && dia >= 1) {
        const date = new Date(anio, mes - 1, dia);
        if (date.getFullYear() === anio && date.getMonth() === mes - 1 && date.getDate() === dia) {
          return date;
        }
      }
    }
  }

  return null;
}
